import { Head, router, useForm } from "@inertiajs/react"
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Save,
  FileText,
  Link2,
  Video,
  HelpCircle,
  FolderOpen,
  ChevronRight,
} from "lucide-react"
import { useState, useCallback, useRef, useEffect } from "react"
import { CoachLayout } from "../../components/CoachLayout"
import { LessonPreview } from "@/pages/App/Admin/Courses/Lessons/LessonPreview"
import { FileUploadField } from "@/components/ui/file-upload-field"
import { buildVideoEmbedUrl } from "@/lib/video-embed"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { RichTextEditor } from "@/components/ui/rich-text-editor/RichTextEditor"

// Coach lesson-detail editor. Ported from the admin lesson page
// (App/Admin/Courses/Lessons/Show.jsx) with every endpoint rewritten from
// /app/admin/... to /app/coach/... and the coach layout. The coach
// course_lessons_controller#show renders THIS page; before it existed, the
// lesson-create redirect resolved to nothing (stuck spinner + duplicate
// lessons) and lesson rows were unclickable (tasks #337/#338). Coach lesson
// editing is owner-only — the controller scopes courses to
// current_account.created_courses.

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseVideoEmbed(url) {
  const embedUrl = buildVideoEmbedUrl(url)
  if (!embedUrl) return null
  return { type: embedUrl.includes("vimeo.com") ? "vimeo" : "youtube", embedUrl }
}

function ResourceTypeBadge({ type }) {
  const config = {
    file: { label: "File", className: "border-blue-500 text-blue-600", icon: FileText },
    link: { label: "Link", className: "border-green-500 text-green-600", icon: Link2 },
    video: { label: "Video", className: "border-purple-500 text-purple-600", icon: Video },
  }
  const c = config[type] || config.link
  const Icon = c.icon
  return (
    <Badge variant="outline" className={c.className}>
      <Icon className="h-3 w-3 mr-1" />
      {c.label}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Question Form Dialog
// ---------------------------------------------------------------------------

function QuestionFormDialog({ open, onOpenChange, courseId, lessonId, question = null, nextPosition = 1 }) {
  const isEditing = !!question
  const form = useForm({
    text: question?.text || "",
    position: question?.position ?? (isEditing ? "" : nextPosition),
  })

  useEffect(() => {
    if (open && !isEditing) {
      form.setData("position", String(nextPosition))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditing, nextPosition])

  const handleSubmit = (e) => {
    e.preventDefault()
    const options = {
      preserveScroll: true,
      onSuccess: () => {
        onOpenChange(false)
        if (!isEditing) form.reset()
      },
    }

    if (isEditing) {
      form.patch(
        `/app/coach/courses/${courseId}/lessons/${lessonId}/questions/${question.id}`,
        options
      )
    } else {
      form.post(
        `/app/coach/courses/${courseId}/lessons/${lessonId}/questions`,
        options
      )
    }
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              {isEditing ? "Edit Question" : "Add Question"}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {isEditing
                ? "Update the question details."
                : "Add a new question for this lesson."}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            <div className="space-y-2">
              <Label htmlFor="question-text">
                Question Text <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="question-text"
                placeholder="Enter the question..."
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 min-h-[100px]"
                value={form.data.text}
                onChange={(e) => form.setData("text", e.target.value)}
                required
              />
              <p className="text-xs text-zinc-500">
                Members see the full question exactly as you type it. Use the
                Preview tab to see the member&apos;s view.
              </p>
              {form.errors.text && (
                <p className="text-sm text-red-600">{form.errors.text}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="question-position">Position</Label>
              <Input
                id="question-position"
                type="number"
                min="0"
                placeholder="e.g. 1"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.position}
                onChange={(e) => form.setData("position", e.target.value)}
              />
            </div>
          </div>

          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </ResponsiveDialogClose>
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing}
            >
              {form.processing
                ? isEditing
                  ? "Saving..."
                  : "Adding..."
                : isEditing
                  ? "Save Changes"
                  : "Add Question"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

// ---------------------------------------------------------------------------
// Resource Form Dialog
// ---------------------------------------------------------------------------

function ResourceFormDialog({ open, onOpenChange, courseId, lessonId, resource = null, nextPosition = 1 }) {
  const isEditing = !!resource
  const form = useForm({
    title: resource?.title || "",
    resource_type: resource?.resourceType || "link",
    url: resource?.url || "",
    file: null,
    position: resource?.position ?? (isEditing ? "" : nextPosition),
  })

  useEffect(() => {
    if (open && !isEditing) {
      form.setData("position", String(nextPosition))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditing, nextPosition])

  const handleSubmit = (e) => {
    e.preventDefault()
    const options = {
      preserveScroll: true,
      forceFormData: form.data.resource_type === "file",
      onSuccess: () => {
        onOpenChange(false)
        if (!isEditing) form.reset()
      },
    }

    if (isEditing) {
      form.patch(
        `/app/coach/courses/${courseId}/lessons/${lessonId}/resources/${resource.id}`,
        options
      )
    } else {
      form.post(
        `/app/coach/courses/${courseId}/lessons/${lessonId}/resources`,
        options
      )
    }
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              {isEditing ? "Edit Resource" : "Add Resource"}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {isEditing
                ? "Update the resource details."
                : "Add a new resource for this lesson."}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            <div className="space-y-2">
              <Label htmlFor="resource-title">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="resource-title"
                placeholder="Resource title"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.title}
                onChange={(e) => form.setData("title", e.target.value)}
                required
              />
              {form.errors.title && (
                <p className="text-sm text-red-600">{form.errors.title}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={form.data.resource_type}
                onValueChange={(v) => {
                  form.setData("resource_type", v)
                }}
              >
                <SelectTrigger className="bg-white border-zinc-300 text-zinc-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="file">File</SelectItem>
                  <SelectItem value="link">Link</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.data.resource_type === "file" ? (
              <div className="space-y-2">
                <Label htmlFor="resource-file">File</Label>
                <FileUploadField
                  id="resource-file"
                  value={form.data.file}
                  onChange={(f) => form.setData("file", f)}
                  existingFileName={isEditing ? resource?.fileName : null}
                  existingFileUrl={isEditing ? resource?.fileUrl : null}
                  helpText="Max 50 MB."
                />
                {form.errors.file && (
                  <p className="text-sm text-red-600">{form.errors.file}</p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="resource-url">
                  URL <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="resource-url"
                  type="url"
                  placeholder={
                    form.data.resource_type === "video"
                      ? "https://youtube.com/watch?v=..."
                      : "https://example.com/resource"
                  }
                  className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                  value={form.data.url}
                  onChange={(e) => form.setData("url", e.target.value)}
                  required
                />
                {form.errors.url && (
                  <p className="text-sm text-red-600">{form.errors.url}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="resource-position">Position</Label>
              <Input
                id="resource-position"
                type="number"
                min="0"
                placeholder="e.g. 1"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.position}
                onChange={(e) => form.setData("position", e.target.value)}
              />
            </div>
          </div>

          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </ResponsiveDialogClose>
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing}
            >
              {form.processing
                ? isEditing
                  ? "Saving..."
                  : "Adding..."
                : isEditing
                  ? "Save Changes"
                  : "Add Resource"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function LessonShow({
  currentUser,
  course,
  lesson,
}) {
  // --- Lesson content state (local, not Inertia form) ---
  const [title, setTitle] = useState(lesson.title || "")
  const [content, setContent] = useState(lesson.content || "")
  const [videoUrl, setVideoUrl] = useState(lesson.videoUrl || "")
  const [status, setStatus] = useState(lesson.status || "draft")
  const [coachOnly, setCoachOnly] = useState(lesson.coachOnly || false)
  const [mediaKind, setMediaKind] = useState(lesson.mediaKind || "vimeo")
  const [mediaFile, setMediaFile] = useState(null)
  const [pendingPurge, setPendingPurge] = useState(false)
  const [saving, setSaving] = useState(false)
  const [viewMode, setViewMode] = useState("edit")
  const submittingRef = useRef(false)

  const [isDirty, setIsDirty] = useState(false)

  const initialValues = useRef({
    title: lesson.title || "",
    content: lesson.content || "",
    videoUrl: lesson.videoUrl || "",
    status: lesson.status || "draft",
    coachOnly: lesson.coachOnly || false,
    mediaKind: lesson.mediaKind || "vimeo",
  })

  useEffect(() => {
    const init = initialValues.current
    const dirty =
      title !== init.title ||
      content !== init.content ||
      videoUrl !== init.videoUrl ||
      status !== init.status ||
      coachOnly !== init.coachOnly ||
      mediaKind !== init.mediaKind ||
      !!mediaFile ||
      pendingPurge
    setIsDirty(dirty)
  }, [title, content, videoUrl, status, coachOnly, mediaKind, mediaFile, pendingPurge])

  useEffect(() => {
    setTitle(lesson.title || "")
    setContent(lesson.content || "")
    setVideoUrl(lesson.videoUrl || "")
    setStatus(lesson.status || "draft")
    setCoachOnly(lesson.coachOnly || false)
    setMediaKind(lesson.mediaKind || "vimeo")
    setMediaFile(null)
    setPendingPurge(false)
    initialValues.current = {
      title: lesson.title || "",
      content: lesson.content || "",
      videoUrl: lesson.videoUrl || "",
      status: lesson.status || "draft",
      coachOnly: lesson.coachOnly || false,
      mediaKind: lesson.mediaKind || "vimeo",
    }
    setIsDirty(false)
  }, [lesson.id, lesson.title, lesson.content, lesson.videoUrl, lesson.status, lesson.coachOnly, lesson.mediaKind])

  const handleContentChange = useCallback((html) => {
    setContent(html)
  }, [])

  const handleSave = () => {
    setSaving(true)
    submittingRef.current = true
    const payload = {
      title,
      content,
      video_url: mediaKind === "pdf" ? "" : videoUrl,
      status,
      coach_only: coachOnly,
      media_kind: mediaKind,
      _redirect: "lesson",
      _method: "patch",
    }
    if (mediaKind === "pdf" && mediaFile) {
      payload.media_file = mediaFile
    }
    if (mediaKind === "pdf" && pendingPurge && !mediaFile) {
      payload._purge_media_file = "1"
    }

    router.post(
      `/app/coach/courses/${course.id}/lessons/${lesson.id}`,
      payload,
      {
        forceFormData: true,
        preserveState: true,
        preserveScroll: true,
        onFinish: () => {
          setSaving(false)
          setMediaFile(null)
          setPendingPurge(false)
          submittingRef.current = false
        },
      }
    )
  }

  useEffect(() => {
    const removeListener = router.on("before", (event) => {
      if (submittingRef.current) return
      if (isDirty) {
        if (!window.confirm("You have unsaved changes. Are you sure you want to leave?")) {
          event.preventDefault()
        }
      }
    })
    return removeListener
  }, [isDirty])

  // --- Question state ---
  const [addQuestionOpen, setAddQuestionOpen] = useState(false)
  const [editQuestion, setEditQuestion] = useState(null)
  const [editQuestionOpen, setEditQuestionOpen] = useState(false)
  const [deleteQuestion, setDeleteQuestion] = useState(null)

  const handleDeleteQuestion = () => {
    if (!deleteQuestion) return
    router.delete(
      `/app/coach/courses/${course.id}/lessons/${lesson.id}/questions/${deleteQuestion.id}`,
      {
        preserveScroll: true,
        onSuccess: () => setDeleteQuestion(null),
      }
    )
  }

  // --- Resource state ---
  const [addResourceOpen, setAddResourceOpen] = useState(false)
  const [editResource, setEditResource] = useState(null)
  const [editResourceOpen, setEditResourceOpen] = useState(false)
  const [deleteResource, setDeleteResource] = useState(null)

  const handleDeleteResource = () => {
    if (!deleteResource) return
    router.delete(
      `/app/coach/courses/${course.id}/lessons/${lesson.id}/resources/${deleteResource.id}`,
      {
        preserveScroll: true,
        onSuccess: () => setDeleteResource(null),
      }
    )
  }

  const videoEmbed = parseVideoEmbed(videoUrl)

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title={`${lesson.title} - Kingdom Factor`} />

      <div className="space-y-6">
        {/* Section 1 -- Lesson Content */}
        <div>
          <nav className="flex items-center gap-1 text-sm text-zinc-500 mb-4">
            <button
              onClick={() => router.visit("/app/coach/courses")}
              className="hover:text-zinc-700 transition-colors"
            >
              Courses
            </button>
            <ChevronRight className="h-3 w-3" />
            <button
              onClick={() =>
                router.visit(`/app/coach/courses/${course.id}`)
              }
              className="hover:text-zinc-700 transition-colors"
            >
              {course.title}
            </button>
            <ChevronRight className="h-3 w-3" />
            <span className="text-zinc-900 font-medium">{lesson.title}</span>
          </nav>

          <button
            onClick={() =>
              router.visit(`/app/coach/courses/${course.id}`)
            }
            className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700 mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to {course.title}
          </button>

          <Card className="bg-white border-zinc-200">
            <CardHeader className="pb-0">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <CardTitle className="text-lg font-semibold text-zinc-900">
                  Lesson Content
                </CardTitle>
                <div className="inline-flex rounded-md border border-zinc-200 bg-zinc-50 p-0.5 text-sm">
                  {[
                    { value: "edit", label: "Edit" },
                    { value: "preview", label: "Preview" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setViewMode(opt.value)}
                      className={`px-3 py-1 rounded transition-colors ${
                        viewMode === opt.value
                          ? "bg-white text-zinc-900 shadow-sm font-medium"
                          : "text-zinc-500 hover:text-zinc-700"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              {viewMode === "preview" ? (
                <LessonPreview
                  title={title}
                  content={content}
                  coachOnly={coachOnly}
                  mediaKind={mediaKind}
                  videoEmbed={videoEmbed}
                  mediaFile={mediaFile}
                  existingPdfUrl={lesson.mediaFileUrl}
                  questions={lesson.questions || []}
                  resources={lesson.resources || []}
                />
              ) : (
                <>
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="lesson-title">
                  Title <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="lesson-title"
                  placeholder="Lesson title"
                  className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              {/* Content (TipTap) */}
              <div className="space-y-2">
                <Label>Content</Label>
                <RichTextEditor
                  value={content}
                  onChange={handleContentChange}
                  placeholder="Write the lesson content..."
                />
              </div>

              {/* Media Kind */}
              <div className="space-y-2">
                <Label>Media Type</Label>
                <div className="flex flex-wrap gap-4">
                  {[
                    { value: "vimeo", label: "Vimeo" },
                    { value: "youtube", label: "YouTube" },
                    { value: "pdf", label: "PDF" },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="media_kind"
                        value={opt.value}
                        checked={mediaKind === opt.value}
                        onChange={() => setMediaKind(() => opt.value)}
                        className="accent-kf-red"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Video URL or PDF upload */}
              {mediaKind === "pdf" ? (
                <div className="space-y-2">
                  <Label htmlFor="lesson-media-file">PDF File</Label>
                  <FileUploadField
                    id="lesson-media-file"
                    accept="application/pdf"
                    value={mediaFile}
                    onChange={setMediaFile}
                    existingFileName={lesson.mediaFileName}
                    existingFileUrl={lesson.mediaFileUrl}
                    pendingRemoval={pendingPurge}
                    onRequestRemoveExisting={() => setPendingPurge(true)}
                    onUndoRemoveExisting={() => setPendingPurge(false)}
                    helpText="PDF only. Max 50 MB."
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="lesson-video-url">Video URL</Label>
                  <Input
                    id="lesson-video-url"
                    type="url"
                    placeholder={
                      mediaKind === "youtube"
                        ? "https://youtube.com/watch?v=..."
                        : "https://vimeo.com/..."
                    }
                    className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                  />
                  <p className="text-xs text-zinc-400">
                    {mediaKind === "youtube"
                      ? "Paste a YouTube URL."
                      : "Paste a Vimeo URL."}
                  </p>
                  {videoEmbed && (
                    <div className="mt-3 rounded-lg overflow-hidden border border-zinc-200">
                      <iframe
                        src={videoEmbed.embedUrl}
                        title="Video preview"
                        className="w-full aspect-video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Status */}
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="bg-white border-zinc-300 text-zinc-900 w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Coach Only */}
              <div className="flex items-center justify-between max-w-sm">
                <div className="space-y-0.5">
                  <Label>Coach Only</Label>
                  <p className="text-xs text-zinc-400">
                    Only visible to coaches when enabled
                  </p>
                </div>
                <Switch
                  checked={coachOnly}
                  onCheckedChange={setCoachOnly}
                />
              </div>

              {/* Save Button */}
              <div className="flex items-center gap-3 pt-2">
                <Button
                  variant="gradient"
                  onClick={handleSave}
                  disabled={saving || !isDirty}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? "Saving..." : "Save Lesson"}
                </Button>
                {isDirty && (
                  <span className="text-sm text-amber-600">
                    You have unsaved changes
                  </span>
                )}
              </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Section 2 -- Questions */}
        {viewMode === "edit" && (
        <Card className="bg-white border-zinc-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-zinc-900">
                Questions
              </CardTitle>
              <Button
                variant="gradient"
                size="sm"
                onClick={() => setAddQuestionOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Question
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {lesson.questions?.length > 0 ? (
              <div className="divide-y divide-zinc-100">
                {lesson.questions.map((q) => (
                  <div
                    key={q.id}
                    className="flex items-start justify-between gap-3 px-6 py-3 hover:bg-zinc-50"
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <span className="text-xs text-zinc-400 font-mono mt-0.5 shrink-0">
                        #{q.position ?? "-"}
                      </span>
                      <p className="text-sm text-zinc-700 whitespace-pre-wrap break-words">
                        {q.text}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          setEditQuestion(q)
                          setEditQuestionOpen(true)
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => setDeleteQuestion(q)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-400">
                <HelpCircle className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">No questions yet</p>
                <p className="text-xs text-zinc-400 mt-1">
                  Add questions for members to answer after completing this lesson.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Section 3 -- Resources */}
        {viewMode === "edit" && (
        <Card className="bg-white border-zinc-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-zinc-900">
                Resources
              </CardTitle>
              <Button
                variant="gradient"
                size="sm"
                onClick={() => setAddResourceOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Resource
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {lesson.resources?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-200 hover:bg-transparent">
                    <TableHead className="text-zinc-500 w-16">Pos</TableHead>
                    <TableHead className="text-zinc-500">Title</TableHead>
                    <TableHead className="text-zinc-500">Type</TableHead>
                    <TableHead className="text-zinc-500 hidden sm:table-cell">
                      URL / File
                    </TableHead>
                    <TableHead className="text-zinc-500 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lesson.resources.map((r) => (
                    <TableRow
                      key={r.id}
                      className="border-zinc-100 hover:bg-zinc-50"
                    >
                      <TableCell className="text-zinc-500 font-mono text-xs">
                        {r.position ?? "-"}
                      </TableCell>
                      <TableCell className="font-medium text-zinc-900">
                        {r.title}
                      </TableCell>
                      <TableCell>
                        <ResourceTypeBadge type={r.resourceType} />
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-zinc-500 text-sm max-w-[200px] truncate">
                        {r.resourceType === "file" ? (
                          r.fileUrl ? (
                            <a
                              href={r.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              View file
                            </a>
                          ) : (
                            <span className="text-zinc-400">No file</span>
                          )
                        ) : r.url ? (
                          <a
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {r.url}
                          </a>
                        ) : (
                          <span className="text-zinc-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                              setEditResource(r)
                              setEditResourceOpen(true)
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => setDeleteResource(r)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-zinc-400">
                <FolderOpen className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">No resources yet</p>
                <p className="text-xs text-zinc-400 mt-1">
                  Add files, links, or videos for this lesson.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      {/* Dialogs */}

      <QuestionFormDialog
        open={addQuestionOpen}
        onOpenChange={setAddQuestionOpen}
        courseId={course.id}
        lessonId={lesson.id}
        nextPosition={
          (lesson.questions?.reduce((max, q) => Math.max(max, q.position ?? 0), 0) ?? 0) + 1
        }
      />

      <QuestionFormDialog
        key={editQuestion?.id ?? "none"}
        open={editQuestionOpen}
        onOpenChange={(open) => !open && setEditQuestionOpen(false)}
        courseId={course.id}
        lessonId={lesson.id}
        question={editQuestion}
      />

      <AlertDialog
        open={!!deleteQuestion}
        onOpenChange={(open) => !open && setDeleteQuestion(null)}
      >
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Question</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this question? Any responses to this
              question will also be removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteQuestion}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Question
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ResourceFormDialog
        open={addResourceOpen}
        onOpenChange={setAddResourceOpen}
        courseId={course.id}
        lessonId={lesson.id}
        nextPosition={
          (lesson.resources?.reduce((max, r) => Math.max(max, r.position ?? 0), 0) ?? 0) + 1
        }
      />

      {/* Keyed by resource id: useForm seeds its data once per mount, so a
          fresh mount is what makes the dialog show the row you clicked. Keep
          the row latched in state while closing so the exit animation has
          content — nulling it here blanks the dialog mid-transition. */}
      <ResourceFormDialog
        key={editResource?.id ?? "none"}
        open={editResourceOpen}
        onOpenChange={(open) => !open && setEditResourceOpen(false)}
        courseId={course.id}
        lessonId={lesson.id}
        resource={editResource}
      />

      <AlertDialog
        open={!!deleteResource}
        onOpenChange={(open) => !open && setDeleteResource(null)}
      >
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Resource</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{deleteResource?.title}
              &rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteResource}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Resource
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CoachLayout>
  )
}
